import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReport } from '../../hooks/useReports';
import { useAuthStore } from '../../stores/auth.store';
import DataTable from '../../components/common/DataTable';

const REPORT_TYPES = [
  { value: 'sales', label: 'Sales Report' },
  { value: 'inventory', label: 'Inventory Report' },
  { value: 'customers', label: 'Customer Report' },
  { value: 'staff', label: 'Staff Performance' },
  { value: 'expenses', label: 'Expenses Report' },
  { value: 'profit', label: 'Profit & Loss' },
];

export default function ReportsPage() {
  const { t } = useTranslation();
  const account = useAuthStore((s) => s.account);
  const shop = useAuthStore((s) => s.shop);
  const generateReport = useReport();

  const today = new Date().toISOString().split('T')[0];
  const firstOfMonth = today.slice(0, 7) + '-01';

  const [reportType, setReportType] = useState('sales');
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [error, setError] = useState('');

  const resultData = generateReport.data?.data as unknown[] | undefined;

  // Try to extract column headers from first row
  const columns = resultData && resultData.length > 0
    ? Object.keys(resultData[0] as Record<string, unknown>).map((key) => ({
        header: key,
        accessor: key,
      }))
    : [];

  async function handleGenerate(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    try {
      await generateReport.mutateAsync({
        type: reportType,
        shopid: shop?.id,
        accountid: account?.id,
        from,
        to,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate report');
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">{t('reports', 'Reports')}</h1>
      </div>

      <div className="report-form form-card">
        <form onSubmit={handleGenerate}>
          <div className="form-grid form-grid--4col">
            <div className="form-group">
              <label className="form-label">Report Type</label>
              <select
                className="form-select"
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
              >
                {REPORT_TYPES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">From</label>
              <input
                type="date"
                className="form-input"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">To</label>
              <input
                type="date"
                className="form-input"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                required
              />
            </div>

            <div className="form-group form-group--end">
              <button
                type="submit"
                className="btn btn--primary"
                disabled={generateReport.isPending}
              >
                {generateReport.isPending ? 'Generating...' : t('export', 'Generate')}
              </button>
            </div>
          </div>

          {error && <div className="alert alert--error">{error}</div>}
        </form>
      </div>

      {/* Results */}
      {resultData && (
        <div className="report-results">
          <div className="report-results__header">
            <h2>Results ({resultData.length} rows)</h2>
          </div>
          {columns.length > 0 ? (
            <DataTable
              columns={columns}
              data={resultData as Record<string, unknown>[]}
              emptyMessage="No data"
            />
          ) : (
            <pre className="report-raw">{JSON.stringify(resultData, null, 2)}</pre>
          )}
        </div>
      )}
    </div>
  );
}
